/**
 * Parcerias V1 — Hub-and-spoke landing
 *
 * Live data via `useParceriasSummary` (monumenta-trpc parcerias router, MONU-74).
 * No mock data — backend is deployed.
 *
 * Layout:
 *   ┌── Parcerias ───────────────────────────────────────────────────┐
 *   │ KPIs: [Repasses no mês] [Parceiros] [Clientes] [Ticket médio]  │
 *   │ Tabs: [Por Cliente] [Por Parceiro]                             │
 *   │ List: clickable rows → /financial/parcerias/<axis>/<id>        │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Stakeholder spec: Vanessa, "Centralização de controles financeiros" 2026-05-08.
 * Predecessor of MONU-30 (V2 Márcia data entry).
 */

import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  HeartHandshake, Search, X, Users, Building2, Coins, Receipt,
  ChevronRight, AlertTriangle, Sparkles,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { usePageTitle } from '@/stores/page-title-store'
import { SensitiveValue } from '@/components/privacy/SensitiveText'
import { useParceriasSummary } from '@/api/parcerias-hooks'
import {
  PeriodPresetSelector,
  calculatePeriodDates,
  type PeriodDates,
} from '../dre/components/PeriodPresetSelector'
import type {
  ClienteRow,
  ParceiroRow,
} from '@/api/parcerias-router.types'

// ============================================================================
// HELPERS
// ============================================================================

function formatCurrencyBR(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatCompact(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}M`
  }
  if (abs >= 1_000) {
    return `R$ ${(value / 1_000).toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    })}K`
  }
  return formatCurrencyBR(value)
}

function formatNumber(value: number): string {
  return value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })
}

/** Convert ISO date string YYYY-MM-DD → Protheus YYYYMMDD. */
function isoToProtheus(iso: string): string {
  return iso.replace(/-/g, '')
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function KpiCard({
  label,
  value,
  hint,
  icon: Icon,
  color = 'text-white',
  sensitive = false,
  loading = false,
}: {
  label: string
  value: string
  hint?: string
  icon: typeof HeartHandshake
  color?: string
  sensitive?: boolean
  loading?: boolean
}) {
  return (
    <div className="metric-card relative">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-medium text-brand-gray uppercase tracking-wide">
          {label}
        </span>
        <Icon size={14} className="text-brand-gray/50" />
      </div>
      {loading ? (
        <div className="mt-2 h-8 w-20 rounded bg-brand-dark-lighter animate-pulse" />
      ) : (
        <div className={`text-2xl font-bold font-display mt-2 ${color}`}>
          {sensitive ? (
            <SensitiveValue mask="R$ ••••••">{value}</SensitiveValue>
          ) : (
            value
          )}
        </div>
      )}
      {hint && !loading && (
        <p className="text-brand-gray/60 text-xs mt-1.5">{hint}</p>
      )}
    </div>
  )
}

function AxisTab({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean
  onClick: () => void
  label: string
  count: number
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer border-b-2 -mb-px ${
        active
          ? 'text-brand-primary border-brand-primary'
          : 'text-brand-gray hover:text-brand-gray-light border-transparent'
      }`}
    >
      <span>{label}</span>
      <span
        className={`ml-2 inline-flex items-center justify-center min-w-[1.5rem] px-1.5 h-5 rounded-full text-[10px] font-semibold ${
          active
            ? 'bg-brand-primary/20 text-brand-primary'
            : 'bg-brand-dark-lighter text-brand-gray/70'
        }`}
      >
        {count}
      </span>
    </button>
  )
}

function ClienteRowCard({
  row,
  rank,
  maxValue,
  onClick,
}: {
  row: ClienteRow
  rank: number
  maxValue: number
  onClick: () => void
}) {
  const pct = maxValue > 0 ? (row.totalParceria / maxValue) * 100 : 0
  return (
    <button
      type="button"
      onClick={onClick}
      className="group w-full text-left bg-[#1E2024] border border-[#2A2D34] rounded-xl p-4 hover:border-brand-primary/40 hover:bg-[#22252B] transition-colors cursor-pointer"
    >
      <div className="flex items-center gap-3">
        <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg bg-brand-dark-lighter text-brand-gray text-xs font-semibold font-display">
          {rank}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-white truncate">
              {row.clienteNome}
            </h3>
            <div className="text-right shrink-0">
              <div className="text-base font-bold font-display text-white">
                <SensitiveValue mask="R$ ••••••">{formatCompact(row.totalParceria)}</SensitiveValue>
              </div>
              <div className="text-[10px] text-brand-gray/60">
                <SensitiveValue mask="R$ ••••••">{formatCurrencyBR(row.totalParceria)}</SensitiveValue>
              </div>
            </div>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-brand-dark-lighter overflow-hidden">
            <div
              className="h-full bg-brand-primary rounded-full transition-all"
              style={{ width: `${Math.max(2, pct)}%` }}
            />
          </div>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-brand-gray/70">
            <span className="inline-flex items-center gap-1">
              <Users size={11} />
              {row.parceirosCount} parceiro{row.parceirosCount !== 1 ? 's' : ''}
            </span>
            <span className="inline-flex items-center gap-1">
              <Receipt size={11} />
              {row.notasCount} nota{row.notasCount !== 1 ? 's' : ''}
            </span>
            <span className="ml-auto text-brand-gray/40">{row.clienteCodigo}</span>
          </div>
        </div>
        <ChevronRight
          size={16}
          className="shrink-0 text-brand-gray/40 group-hover:text-brand-primary transition-colors"
        />
      </div>
    </button>
  )
}

function ParceiroRowCard({
  row,
  rank,
  maxValue,
  onClick,
}: {
  row: ParceiroRow
  rank: number
  maxValue: number
  onClick: () => void
}) {
  const pct = maxValue > 0 ? (row.totalParceria / maxValue) * 100 : 0
  return (
    <button
      type="button"
      onClick={onClick}
      className="group w-full text-left bg-[#1E2024] border border-[#2A2D34] rounded-xl p-4 hover:border-brand-primary/40 hover:bg-[#22252B] transition-colors cursor-pointer"
    >
      <div className="flex items-center gap-3">
        <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg bg-brand-dark-lighter text-brand-gray text-xs font-semibold font-display">
          {rank}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-white truncate">
              {row.parceiroNome}
            </h3>
            <div className="text-right shrink-0">
              <div className="text-base font-bold font-display text-white">
                <SensitiveValue mask="R$ ••••••">{formatCompact(row.totalParceria)}</SensitiveValue>
              </div>
              <div className="text-[10px] text-brand-gray/60">
                <SensitiveValue mask="R$ ••••••">{formatCurrencyBR(row.totalParceria)}</SensitiveValue>
              </div>
            </div>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-brand-dark-lighter overflow-hidden">
            <div
              className="h-full bg-brand-primary rounded-full transition-all"
              style={{ width: `${Math.max(2, pct)}%` }}
            />
          </div>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-brand-gray/70">
            <span className="inline-flex items-center gap-1">
              <Building2 size={11} />
              {row.clientesCount} cliente{row.clientesCount !== 1 ? 's' : ''}
            </span>
            <span className="inline-flex items-center gap-1">
              <Receipt size={11} />
              {row.notasCount} nota{row.notasCount !== 1 ? 's' : ''}
            </span>
            <span className="ml-auto text-brand-gray/40">
              {row.parceiroCodigo}-{row.parceiroLoja}
            </span>
          </div>
        </div>
        <ChevronRight
          size={16}
          className="shrink-0 text-brand-gray/40 group-hover:text-brand-primary transition-colors"
        />
      </div>
    </button>
  )
}

function ListSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="bg-[#1E2024] border border-[#2A2D34] rounded-xl p-4 animate-pulse"
        >
          <div className="h-4 w-2/3 rounded bg-brand-dark-lighter mb-3" />
          <div className="h-1.5 rounded bg-brand-dark-lighter mb-3" />
          <div className="h-3 w-1/2 rounded bg-brand-dark-lighter" />
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function Parcerias() {
  usePageTitle('Parcerias')
  useAuth()
  const navigate = useNavigate()

  const [axis, setAxis] = useState<'cliente' | 'parceiro'>('cliente')
  const [searchQuery, setSearchQuery] = useState('')

  // Period filter (defaults to YTD — same as Pontual's Central de Notas).
  const defaultDates = useMemo(() => calculatePeriodDates('ytd'), [])
  const [periodStartDate, setPeriodStartDate] = useState(defaultDates.startDate)
  const [periodEndDate, setPeriodEndDate] = useState(defaultDates.endDate)

  const handlePeriodChange = useCallback((dates: PeriodDates) => {
    setPeriodStartDate(dates.startDate)
    setPeriodEndDate(dates.endDate)
  }, [])

  const { data: summary, isLoading, error } = useParceriasSummary({
    dataInicial: isoToProtheus(periodStartDate),
    dataFinal: isoToProtheus(periodEndDate),
  })

  const porCliente = summary?.porCliente ?? []
  const porParceiro = summary?.porParceiro ?? []
  const kpis = summary?.kpis ?? {
    totalRepasses: 0,
    totalBaixados: 0,
    totalFallbacks: 0,
    totalParcerias: 0,
    parceirosAtivos: 0,
    clientesAtivos: 0,
    ticketMedio: 0,
  }

  const filteredClientes = useMemo(() => {
    if (!searchQuery.trim()) return porCliente
    const q = searchQuery.toLowerCase()
    return porCliente.filter(
      (c) =>
        c.clienteNome.toLowerCase().includes(q) ||
        c.clienteCodigo.includes(q),
    )
  }, [searchQuery, porCliente])

  const filteredParceiros = useMemo(() => {
    if (!searchQuery.trim()) return porParceiro
    const q = searchQuery.toLowerCase()
    return porParceiro.filter(
      (p) =>
        p.parceiroNome.toLowerCase().includes(q) ||
        p.parceiroCodigo.includes(q),
    )
  }, [searchQuery, porParceiro])

  const maxClienteValue = useMemo(
    () => Math.max(0, ...porCliente.map((c) => c.totalParceria)),
    [porCliente],
  )
  const maxParceiroValue = useMemo(
    () => Math.max(0, ...porParceiro.map((p) => p.totalParceria)),
    [porParceiro],
  )

  const goToCliente = useCallback(
    (codigo: string) => {
      navigate({ to: '/financial/parcerias/$axis/$id', params: { axis: 'cliente', id: codigo } })
    },
    [navigate],
  )
  const goToParceiro = useCallback(
    (codigo: string, loja: string) => {
      navigate({
        to: '/financial/parcerias/$axis/$id',
        params: { axis: 'parceiro', id: `${codigo}-${loja}` },
      })
    },
    [navigate],
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <HeartHandshake size={22} className="text-brand-primary" />
          <h1 className="text-2xl font-bold font-display text-white">Parcerias</h1>
          <span className="px-2 py-0.5 rounded-md bg-brand-primary/15 text-brand-primary text-[10px] font-bold uppercase tracking-wider">
            V1
          </span>
        </div>
        <p className="text-sm text-brand-gray mt-1">
          Repasses a fornecedores parceiros — o que pagamos integralmente para depois recuperar via emissão de NF intercompany.
        </p>
      </div>

      {/* Period filter */}
      <div className="flex items-center gap-4 flex-wrap">
        <span className="text-xs font-semibold text-brand-gray uppercase tracking-wide min-w-[80px]">
          Período
        </span>
        <PeriodPresetSelector
          startDate={periodStartDate}
          endDate={periodEndDate}
          onPeriodChange={handlePeriodChange}
        />
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-red-400">Erro ao carregar parcerias</h3>
            <p className="text-xs text-brand-gray-light mt-1">
              {error instanceof Error ? error.message : 'Não foi possível conectar ao Protheus.'}
            </p>
          </div>
        </div>
      )}

      {/* KPI Hero Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard
          label="Parceria Total"
          value={formatCompact(kpis.totalParcerias)}
          hint={formatCurrencyBR(kpis.totalParcerias)}
          icon={Coins}
          color="text-brand-primary"
          sensitive
          loading={isLoading}
        />
        <KpiCard
          label="Parceria Paga"
          value={formatCompact(kpis.totalRepasses)}
          hint="precisa de NF de terceiros"
          icon={AlertTriangle}
          color="text-yellow-400"
          sensitive
          loading={isLoading}
        />
        <KpiCard
          label="Parceria Baixada"
          value={formatCompact(kpis.totalBaixados)}
          hint="só sobe no gerencial"
          icon={Receipt}
          sensitive
          loading={isLoading}
        />
        <KpiCard
          label="Parceria Fallback"
          value={formatCompact(kpis.totalFallbacks)}
          hint="parceiro+cliente marcado manualmente"
          icon={Sparkles}
          sensitive
          loading={isLoading}
        />
        <KpiCard
          label="Parceiros ativos"
          value={formatNumber(kpis.parceirosAtivos)}
          hint="fornecedores no período"
          icon={Users}
          loading={isLoading}
        />
        <KpiCard
          label="Clientes"
          value={formatNumber(kpis.clientesAtivos)}
          hint="com parcerias no período"
          icon={Building2}
          loading={isLoading}
        />
      </div>

      {/* Tabs + Search */}
      <div className="space-y-4">
        <div className="flex items-end justify-between gap-4 border-b border-brand-dark-lighter">
          <div className="flex items-center">
            <AxisTab
              active={axis === 'cliente'}
              onClick={() => setAxis('cliente')}
              label="Por Cliente"
              count={porCliente.length}
            />
            <AxisTab
              active={axis === 'parceiro'}
              onClick={() => setAxis('parceiro')}
              label="Por Parceiro"
              count={porParceiro.length}
            />
          </div>

          <div className="relative w-full max-w-xs mb-2">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-gray/50 pointer-events-none"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={
                axis === 'cliente' ? 'Buscar cliente...' : 'Buscar parceiro...'
              }
              className="w-full pl-9 pr-9 py-2 text-sm bg-[#1E2024] border border-[#2A2D34] rounded-lg text-white placeholder:text-brand-gray/40 focus:outline-none focus:border-brand-primary/50 transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-brand-gray/50 hover:text-white transition-colors cursor-pointer p-0.5"
                aria-label="Limpar busca"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Result count */}
        {!isLoading && (
          <p className="text-xs text-brand-gray/60">
            {axis === 'cliente'
              ? `${filteredClientes.length} cliente${filteredClientes.length !== 1 ? 's' : ''}`
              : `${filteredParceiros.length} parceiro${filteredParceiros.length !== 1 ? 's' : ''}`}
          </p>
        )}

        {/* List — loading / live */}
        {isLoading ? (
          <ListSkeleton />
        ) : axis === 'cliente' ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filteredClientes.map((row, idx) => (
              <ClienteRowCard
                key={row.clienteCodigo}
                row={row}
                rank={idx + 1}
                maxValue={maxClienteValue}
                onClick={() => goToCliente(row.clienteCodigo)}
              />
            ))}
            {filteredClientes.length === 0 && (
              <div className="lg:col-span-2 text-center py-16 text-brand-gray/60 text-sm">
                Nenhum cliente com parcerias encontrado.
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filteredParceiros.map((row, idx) => (
              <ParceiroRowCard
                key={`${row.parceiroCodigo}-${row.parceiroLoja}`}
                row={row}
                rank={idx + 1}
                maxValue={maxParceiroValue}
                onClick={() => goToParceiro(row.parceiroCodigo, row.parceiroLoja)}
              />
            ))}
            {filteredParceiros.length === 0 && (
              <div className="lg:col-span-2 text-center py-16 text-brand-gray/60 text-sm">
                Nenhum parceiro encontrado.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
